<template>
  <div class="canvas-container">
    <!-- 画布工具栏 -->
    <div class="canvas-toolbar">
      <div class="toolbar-left">
        <div class="zoom-info">
          缩放: {{ zoomLevel }}%
        </div>
        <div class="canvas-size">
          {{ templateConfig.width }} × {{ templateConfig.height }} mm
        </div>
      </div>
      
      <div class="toolbar-center">
        <div class="element-tools">
          <button 
            @click="addTextElement" 
            class="tool-btn"
            title="添加文本"
          >
            <span class="tool-icon">T</span>
            文本
          </button>
          <button 
            @click="addImageElement" 
            class="tool-btn"
            title="添加图片"
          >
            <span class="tool-icon">🖼️</span>
            图片
          </button>
          <button 
            @click="addBarcodeElement" 
            class="tool-btn"
            title="添加条码"
          >
            <span class="tool-icon">📊</span>
            条码
          </button>
          <button 
            @click="addQRCodeElement" 
            class="tool-btn"
            title="添加二维码"
          >
            <span class="tool-icon">⊞</span>
            二维码
          </button>
          <button 
            @click="addLineElement" 
            class="tool-btn"
            title="添加线段"
          >
            <span class="tool-icon">━</span>
            线段
          </button>
        </div>
      </div>
      
      <div class="toolbar-right">
        <div class="selection-info" v-if="selectedElement">
          已选择: {{ getElementDisplayName(selectedElement) }}
        </div>
        <button 
          v-if="selectedElement"
          @click="deleteSelectedElement"
          class="delete-btn"
          title="删除选中元素"
        >
          🗑️ 删除
        </button>
      </div>
    </div>

    <!-- 画布区域 -->
    <div class="canvas-wrapper" ref="canvasWrapper">
      <div 
        class="canvas" 
        :style="canvasStyle"
        @click="handleCanvasClick"
        @dragover.prevent
        @drop="handleCanvasDrop"
      >
        <!-- 尺寸标注 -->
        <div class="dimension-labels">
          <div class="width-label">{{ templateConfig.width }}mm</div>
          <div class="height-label">{{ templateConfig.height }}mm</div>
        </div>
        
        <!-- 打印区域 -->
        <div 
          class="print-area" 
          :style="printAreaStyle"
          ref="printArea"
        >
          <!-- 网格背景 -->
          <div class="grid-background" v-if="showGrid"></div>
          
          <!-- 标尺 -->
          <div class="rulers" v-if="showRulers">
            <div class="ruler ruler-top"></div>
            <div class="ruler ruler-left"></div>
          </div>
          
          <!-- 模板元素 -->
          <div class="template-content">
            <TemplateElement
              v-for="(element, index) in elements"
              :key="element.id || index"
              :element="element"
              :index="index"
              :selected="selectedElementIndex === index"
              :zoom="zoomLevel / 100"
              @select="selectElement"
              @update="updateElement"
              @start-drag="startElementDrag"
              @drag="onElementDrag"
              @end-drag="endElementDrag"
            />
          </div>
          
          <!-- 选择框 -->
          <div 
            v-if="selectionBox.visible"
            class="selection-box"
            :style="selectionBoxStyle"
          ></div>
          
          <!-- 辅助线 -->
          <div class="guide-lines">
            <div 
              v-for="line in guideLines" 
              :key="line.id"
              class="guide-line"
              :class="line.type"
              :style="line.style"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 状态栏 -->
    <div class="status-bar">
      <div class="status-left">
        <span>元素数量: {{ elements.length }}</span>
        <span v-if="selectedElement">
          位置: {{ Math.round(parseFloat(selectedElement.style.left)) }}, 
               {{ Math.round(parseFloat(selectedElement.style.top)) }}
        </span>
      </div>
      
      <div class="status-right">
        <button 
          @click="toggleGrid" 
          :class="{ active: showGrid }"
          class="status-btn"
          title="显示/隐藏网格"
        >
          🔲 网格
        </button>
        <button 
          @click="toggleRulers" 
          :class="{ active: showRulers }"
          class="status-btn"
          title="显示/隐藏标尺"
        >
          📏 标尺
        </button>
        <button 
          @click="toggleSnap" 
          :class="{ active: snapToGrid }"
          class="status-btn"
          title="网格吸附"
        >
          🧲 吸附
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, reactive, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import TemplateElement from './TemplateElement.vue'
import { FieldService } from '@/services/fieldService'

export default {
  name: 'CanvasContainer',
  components: {
    TemplateElement
  },
  
  setup() {
    const store = useStore()
    
    // DOM 引用
    const canvasWrapper = ref(null)
    const printArea = ref(null)
    
    // 本地状态
    const showGrid = ref(true)
    const showRulers = ref(false)
    const snapToGrid = ref(true)
    const gridSize = ref(10)
    
    // 选择框状态
    const selectionBox = reactive({
      visible: false,
      startX: 0,
      startY: 0,
      endX: 0,
      endY: 0
    })
    
    // 辅助线
    const guideLines = ref([])
    
    // 计算属性
    const templateConfig = computed(() => store.state.template.config)
    const zoomLevel = computed(() => store.state.template.zoomLevel)
    const elements = computed(() => store.state.canvas.elements)
    const selectedElementIndex = computed(() => store.state.canvas.selectedElementIndex)
    const selectedElement = computed(() => store.getters['canvas/selectedElement'])
    
    const canvasStyle = computed(() => ({
      transform: `scale(${zoomLevel.value / 100})`,
      transformOrigin: 'center'
    }))
    
    const printAreaStyle = computed(() => store.getters['template/printAreaStyle'])
    
    const selectionBoxStyle = computed(() => ({
      left: Math.min(selectionBox.startX, selectionBox.endX) + 'px',
      top: Math.min(selectionBox.startY, selectionBox.endY) + 'px',
      width: Math.abs(selectionBox.endX - selectionBox.startX) + 'px',
      height: Math.abs(selectionBox.endY - selectionBox.startY) + 'px'
    }))
    
    // 方法
    const handleCanvasClick = (event) => {
      if (event.target === event.currentTarget) {
        store.dispatch('canvas/clearSelection')
      }
    }
    
    const handleCanvasDrop = (event) => {
      const fieldData = event.dataTransfer.getData('application/json')
      if (fieldData) {
        try {
          const field = JSON.parse(fieldData)
          const rect = printArea.value.getBoundingClientRect()
          const x = (event.clientX - rect.left) / (zoomLevel.value / 100)
          const y = (event.clientY - rect.top) / (zoomLevel.value / 100)
          
          addFieldAtPosition(field, { x, y })
        } catch (error) {
          console.error('解析拖拽数据失败:', error)
        }
      }
    }
    
    const addFieldAtPosition = (field, position) => {
      if (field.category === 'table') {
        // 添加表格字段到现有表格或创建新表格
        const tableElement = elements.value.find(el => el.type === 'table')
        if (tableElement) {
          // 添加到现有表格
          addColumnToTable(tableElement, field)
        } else {
          // 创建新表格
          const tableEl = FieldService.createTableElement([field])
          tableEl.style.left = position.x + 'px'
          tableEl.style.top = position.y + 'px'
          store.dispatch('canvas/addElement', tableEl)
        }
      } else {
        // 创建文本元素
        const textElement = FieldService.createTextElement(field, position)
        store.dispatch('canvas/addElement', textElement)
      }
    }
    
    const addColumnToTable = (tableElement, field) => {
      const tableIndex = elements.value.indexOf(tableElement)
      const updatedTable = { ...tableElement }
      
      // 添加列
      updatedTable.columns.push({
        id: field.id,
        title: field.displayName || field.name,
        width: FieldService.getDefaultColumnWidth(field.id)
      })
      
      // 更新数据
      updatedTable.data = FieldService.generateMockTableData(updatedTable.columns)
      
      store.dispatch('canvas/updateElement', { 
        index: tableIndex, 
        element: updatedTable 
      })
    }
    
    const selectElement = (index) => {
      store.dispatch('canvas/selectElement', index)
    }
    
    const updateElement = ({ index, element }) => {
      store.dispatch('canvas/updateElement', { index, element })
    }
    
    const startElementDrag = ({ elementIndex, startPosition }) => {
      store.dispatch('canvas/startDrag', { elementIndex, startPosition })
    }
    
    const onElementDrag = (position) => {
      store.dispatch('canvas/updateDrag', position)
      
      // 网格吸附
      if (snapToGrid.value) {
        position.x = Math.round(position.x / gridSize.value) * gridSize.value
        position.y = Math.round(position.y / gridSize.value) * gridSize.value
      }
      
      // 生成辅助线
      generateGuideLines(position)
    }
    
    const endElementDrag = () => {
      store.dispatch('canvas/endDrag')
      clearGuideLines()
    }
    
    const generateGuideLines = (position) => {
      // 清除旧的辅助线
      guideLines.value = []
      
      // 与其他元素对齐的辅助线
      elements.value.forEach((el, index) => {
        if (index === selectedElementIndex.value) return
        
        const elX = parseFloat(el.style.left) || 0
        const elY = parseFloat(el.style.top) || 0
        
        // 垂直对齐线
        if (Math.abs(position.x - elX) < 5) {
          guideLines.value.push({
            id: `v-${index}`,
            type: 'vertical',
            style: {
              left: elX + 'px',
              top: '0',
              height: '100%'
            }
          })
        }
        
        // 水平对齐线
        if (Math.abs(position.y - elY) < 5) {
          guideLines.value.push({
            id: `h-${index}`,
            type: 'horizontal',
            style: {
              left: '0',
              top: elY + 'px',
              width: '100%'
            }
          })
        }
      })
    }
    
    const clearGuideLines = () => {
      guideLines.value = []
    }
    
    const deleteSelectedElement = () => {
      if (selectedElementIndex.value !== null) {
        store.dispatch('canvas/removeElement', selectedElementIndex.value)
      }
    }
    
    const getElementDisplayName = (element) => {
      if (element.type === 'table') {
        return `表格 (${element.columns.length}列)`
      } else if (element.type === 'text') {
        return `文本: ${element.content}`
      }
      return '未知元素'
    }
    
    // 工具方法
    const addTextElement = () => {
      const textElement = {
        id: `text_${Date.now()}`,
        type: 'text',
        content: '新文本',
        style: {
          position: 'absolute',
          left: '50px',
          top: '50px',
          fontSize: '14px',
          fontFamily: '微软雅黑',
          color: '#333',
          padding: '4px 8px',
          border: '1px dashed #ccc',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          cursor: 'move'
        }
      }
      store.dispatch('canvas/addElement', textElement)
    }
    
    const addImageElement = () => {
      // TODO: 实现图片元素添加
      console.log('添加图片元素')
    }
    
    const addBarcodeElement = () => {
      const barcodeElement = {
        id: `barcode_${Date.now()}`,
        type: 'barcode',
        content: '123456789012',
        style: {
          position: 'absolute',
          left: '50px',
          top: '50px',
          width: '120px',
          height: '40px',
          border: '1px dashed #999',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          cursor: 'move'
        }
      }
      
      store.dispatch('canvas/addElement', barcodeElement)
    }
    
    const addQRCodeElement = () => {
      const qrCodeElement = {
        id: `qrcode_${Date.now()}`,
        type: 'qrcode',
        content: 'https://example.com',
        style: {
          position: 'absolute',
          left: '100px',
          top: '100px',
          width: '80px',
          height: '80px',
          border: '1px dashed #999',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          cursor: 'move'
        }
      }
      
      store.dispatch('canvas/addElement', qrCodeElement)
    }
    
    const addLineElement = () => {
      // TODO: 实现线段元素添加
      console.log('添加线段元素')
    }
    
    const toggleGrid = () => {
      showGrid.value = !showGrid.value
    }
    
    const toggleRulers = () => {
      showRulers.value = !showRulers.value
    }
    
    const toggleSnap = () => {
      snapToGrid.value = !snapToGrid.value
    }
    
    // 键盘事件处理
    const handleKeyDown = (event) => {
      if (event.key === 'Delete' && selectedElement.value) {
        deleteSelectedElement()
      } else if (event.key === 'Escape') {
        store.dispatch('canvas/clearSelection')
      } else if (event.ctrlKey && event.key === 'z') {
        event.preventDefault()
        store.dispatch('canvas/undo')
      } else if (event.ctrlKey && event.key === 'y') {
        event.preventDefault()
        store.dispatch('canvas/redo')
      }
    }
    
    onMounted(() => {
      document.addEventListener('keydown', handleKeyDown)
    })
    
    onUnmounted(() => {
      document.removeEventListener('keydown', handleKeyDown)
    })
    
    return {
      canvasWrapper,
      printArea,
      showGrid,
      showRulers,
      snapToGrid,
      selectionBox,
      guideLines,
      templateConfig,
      zoomLevel,
      elements,
      selectedElementIndex,
      selectedElement,
      canvasStyle,
      printAreaStyle,
      selectionBoxStyle,
      handleCanvasClick,
      handleCanvasDrop,
      selectElement,
      updateElement,
      startElementDrag,
      onElementDrag,
      endElementDrag,
      deleteSelectedElement,
      getElementDisplayName,
      addTextElement,
      addImageElement,
      addBarcodeElement,
      addQRCodeElement,
      addLineElement,
      toggleGrid,
      toggleRulers,
      toggleSnap
    }
  }
}
</script>

<style scoped>
.canvas-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
}

/* 画布工具栏 */
.canvas-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #dee2e6;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.toolbar-left,
.toolbar-center,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.zoom-info,
.canvas-size {
  font-size: 14px;
  color: #6c757d;
  background: #f8f9fa;
  padding: 4px 8px;
  border-radius: 4px;
}

.element-tools {
  display: flex;
  gap: 8px;
}

.tool-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.tool-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.tool-icon {
  font-size: 16px;
}

.selection-info {
  font-size: 14px;
  color: #495057;
  font-weight: 500;
}

.delete-btn {
  padding: 6px 12px;
  background: #dc3545;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.delete-btn:hover {
  background: #c82333;
}

/* 画布区域 */
.canvas-wrapper {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: auto;
  padding: 40px 20px 60px 20px;
  background: #e9ecef;
  scroll-padding-top: 20px;
  scroll-behavior: smooth;
}

.canvas {
  position: relative;
  transform-origin: center;
  transition: transform 0.2s ease;
  margin: 20px 0;
  min-height: 100px;
}

.dimension-labels {
  position: absolute;
  top: -30px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  pointer-events: none;
}

.width-label {
  background: #28a745;
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
}

.height-label {
  background: #28a745;
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  writing-mode: vertical-rl;
  position: absolute;
  right: -50px;
  top: 50%;
  transform: translateY(-50%);
}

.print-area {
  position: relative;
  background: #fff;
  border: 2px solid #28a745;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  overflow: hidden;
}

.grid-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px);
  background-size: 10px 10px;
  pointer-events: none;
}

.rulers {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.ruler {
  position: absolute;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
}

.ruler-top {
  top: -20px;
  left: 0;
  right: 0;
  height: 20px;
}

.ruler-left {
  left: -20px;
  top: 0;
  bottom: 0;
  width: 20px;
}

.template-content {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 200px;
}

.selection-box {
  position: absolute;
  border: 2px dashed #007bff;
  background: rgba(0, 123, 255, 0.1);
  pointer-events: none;
  z-index: 1000;
}

.guide-lines {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 999;
}

.guide-line {
  position: absolute;
  background: #ff6b6b;
  opacity: 0.8;
}

.guide-line.vertical {
  width: 1px;
}

.guide-line.horizontal {
  height: 1px;
}

/* 状态栏 */
.status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background: #f8f9fa;
  border-top: 1px solid #dee2e6;
  font-size: 12px;
  color: #6c757d;
}

.status-left,
.status-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-btn {
  padding: 4px 8px;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.status-btn:hover {
  background: #e9ecef;
}

.status-btn.active {
  background: #28a745;
  color: #fff;
  border-color: #28a745;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .canvas-toolbar {
    flex-direction: column;
    gap: 8px;
    padding: 8px;
  }
  
  .toolbar-left,
  .toolbar-center,
  .toolbar-right {
    width: 100%;
    justify-content: center;
  }
  
  .canvas-wrapper {
    padding: 20px 10px;
  }
  
  .status-bar {
    flex-direction: column;
    gap: 8px;
  }
  
  .status-left,
  .status-right {
    width: 100%;
    justify-content: center;
  }
}
</style>
